import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatRelative } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Empty, Field, Input, Modal, Select, Spinner } from '../components/ui';

/**
 * Magewell: every encoder and decoder on the Magewell Control Hub. The hub
 * drives them through the Control Hub, so none of them needs a route to the
 * hub or the hub to them. Encoders' destinations and decoders' sources are
 * set here, and connected to MediaConnect flows or straight to each other.
 * Open shows a device's own web page through the Control Hub's tunnel.
 */

type Kind = 'encoder' | 'decoder' | 'other';

interface MwDevice {
  sn: string; name: string; model: string; kind: Kind; online: boolean; ip: string; firmware: string;
  input: string; warns: number; upTime: number | null;
}

interface MwLink {
  id: string; kind: 'encoder-flow' | 'flow-decoder' | 'bridge' | 'direct';
  encoderSn: string | null; serverName: string | null; decoderSn: string | null; presetName: string | null;
  flowId: string | null; flowName: string | null; flowOutput: string | null; outputMade: boolean; flowMade: boolean;
  detail: Record<string, unknown>; createdAt: string; createdBy: string | null;
}

interface Payload { configured: boolean; error: string | null; devices: MwDevice[]; links: MwLink[]; pending?: Record<string, number>; canOpen?: boolean }

interface Pending { id: string; op: string; what: string; error: string | null; tries: number; createdAt: string; createdBy: string | null }

/** GET /magewell/:sn: live, or (offline) what was last read, with what is waiting for it. */
interface DeviceRead<T> { detail: T | null; stale?: boolean; detailAt?: string | null; pending: Pending[]; error?: string }

const OP_LABEL: Record<string, string> = {
  'put-destination': 'Set destination', 'enable-destination': 'Switch destination', 'delete-destination': 'Delete destination',
  'put-source': 'Set source', 'delete-source': 'Delete source', 'select-source': 'Play source',
};

interface Server {
  id: number; name: string; typeName: string; protocol: 'srt-caller' | 'srt-listener' | 'rtmp' | 'other'; enabled: boolean;
  streamIndex: number; host?: string; port?: number; latency?: number; streamId?: string; encrypted: boolean; url?: string; key?: string;
  state: ConnState;
  live: { runMs: number; kbps: number | null; clients: number | null; rttMs: number | null; lossTotal: number | null } | null;
}

interface EncoderDetail {
  input: string; hasSignal: boolean;
  main: { codec: string; width: number; height: number; kbps: number; fps: number | null; followsInput: boolean } | null;
  sub: { codec: string; width: number; height: number; kbps: number } | null;
  servers: Server[];
}

interface SourceRow {
  name: string; protocol: string; host?: string; port?: number; latency?: number; active: boolean; url: string; encrypted: boolean;
}

interface DecoderDetail {
  current: string | null; currentIsNdi: boolean; connected: boolean | null; video: string | null; kbps: number | null; sources: SourceRow[];
  state?: ConnState; droppedVideo?: number; droppedAudio?: number; jitterMs?: number;
}

interface FlowSummary { id: string; name: string; state: string; source: { protocol: string; port: number } }
interface FlowOutput { name: string; protocol: string; port: number; address: string | null; cidr?: string }

/** Whether something is actually connected, as the device says it. */
interface ConnState { tone: 'good' | 'warn' | 'bad' | 'off'; label: string }

/** GET /magewell/status, per serial number. */
interface DeviceStatus extends ConnState {
  destinations?: { name: string; state: ConnState; kbps: number | null }[];
  source?: string | null; video?: string | null; kbps?: number | null;
}

const message = (err: unknown) => (err instanceof ApiError ? err.message : 'Something went wrong.');

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-secondary">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: online ? 'var(--color-good)' : 'var(--color-ink-muted)' }} />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

function Badge({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'good' | 'warning' | 'muted' | 'info' }) {
  const colour = { good: 'var(--color-good)', warning: 'var(--color-warning)', muted: 'var(--color-ink-muted)', info: 'var(--color-series-1)' }[tone];
  return (
    <span className="rounded px-1.5 py-0.5 text-[11px] font-medium" style={{ color: colour, background: `color-mix(in srgb, ${colour} 14%, transparent)` }}>
      {children}
    </span>
  );
}

/** A connection state: the colour is a dot, the words carry the meaning. */
function StateBadge({ state, title }: { state: ConnState; title?: string }) {
  const colour = { good: 'var(--color-good)', warn: 'var(--color-warning)', bad: 'var(--color-critical)', off: 'var(--color-ink-muted)' }[state.tone];
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ color: colour, background: `color-mix(in srgb, ${colour} 14%, transparent)` }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: colour }} />
      {state.label}
    </span>
  );
}

/** The one-line summary for a device in the list. */
function StatusLine({ status }: { status?: DeviceStatus }) {
  if (!status) return null;
  const title = status.destinations?.length
    ? status.destinations.map((d) => `${d.name}: ${d.state.label}${d.kbps ? ` (${d.kbps} kb/s)` : ''}`).join('\n')
    : status.source ? `${status.source}${status.video ? ` · ${status.video}` : ''}${status.kbps ? ` · ${status.kbps} kb/s` : ''}` : undefined;
  return <StateBadge state={status} title={title} />;
}

const fmtUp = (s: number | null) => {
  if (s == null) return '';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};

/** An encoder's input, refreshed every 10 seconds while it is on screen. */
function Thumb({ sn, online, className = 'h-[54px] w-24' }: { sn: string; online: boolean; className?: string }) {
  const [tick, setTick] = useState(() => Date.now());
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!online) return;
    const t = setInterval(() => setTick(Date.now()), 10_000);
    return () => clearInterval(t);
  }, [online]);
  return (
    <div className={`relative shrink-0 overflow-hidden rounded bg-black/60 ring-1 ring-hairline ${className}`}>
      {online && (
        <img
          src={`/api/magewell/${sn}/thumbnail?t=${tick}`}
          alt=""
          className={`h-full w-full object-cover ${failed ? 'invisible' : ''}`}
          onLoad={() => setFailed(false)}
          onError={() => setFailed(true)}
        />
      )}
      {(!online || failed) && (
        <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ink-muted">{online ? 'No picture' : 'Offline'}</span>
      )}
    </div>
  );
}

export function Magewell() {
  const { can } = useAuth();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [connect, setConnect] = useState<Partial<ConnectPreset> | null>(null);
  const [filter, setFilter] = useState('');
  const [tiles, setTiles] = useState(() => { try { return localStorage.getItem('mw-encoder-tiles') === '1'; } catch { return false; } });
  const [tileOpen, setTileOpen] = useState<string | null>(null);
  const [showFavs, setShowFavs] = useState(false);
  const setTileView = (on: boolean) => {
    setTiles(on);
    try { localStorage.setItem('mw-encoder-tiles', on ? '1' : '0'); } catch { /* private window: not remembered */ }
  };

  const load = useCallback((fresh = false) => {
    api.get<Payload>(`/magewell${fresh ? '?fresh=1' : ''}`).then((r) => { setData(r); setError(null); }).catch((err) => setError(message(err)));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 20_000);
    return () => clearInterval(t);
  }, [load]);

  // Whether each online device is actually connected: its own poll, as it asks every device.
  const [status, setStatus] = useState<Record<string, DeviceStatus>>({});
  useEffect(() => {
    let alive = true;
    const poll = () => api.get<{ status: Record<string, DeviceStatus> }>('/magewell/status')
      .then((r) => { if (alive) setStatus(r.status); }).catch(() => undefined);
    void poll();
    const t = setInterval(poll, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const mayControl = can('magewell:control');
  const name = (sn: string | null) => data?.devices.find((d) => d.sn === sn)?.name ?? sn ?? '';

  if (!data && !error) return <Spinner />;

  const shown = (data?.devices ?? []).filter((d) => !filter || `${d.name} ${d.sn} ${d.model} ${d.ip}`.toLowerCase().includes(filter.toLowerCase()));
  const groups: { kind: Kind; title: string }[] = [
    { kind: 'encoder', title: 'Encoders' },
    { kind: 'decoder', title: 'Decoders' },
    { kind: 'other', title: 'Other devices' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Magewell</h1>
          <p className="text-xs text-ink-muted">Encoders and decoders on the Magewell Control Hub, driven from here.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="!w-44" />
          <Button variant="ghost" onClick={() => load(true)}>Refresh</Button>
          {data?.configured && <Button variant="secondary" onClick={() => setShowFavs(true)}>☆ Favourites</Button>}
          {mayControl && data?.configured && <Button variant="primary" onClick={() => setConnect({})}>Connect…</Button>}
        </div>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {data && !data.configured && (
        <Alert tone="warning" title="The Control Hub is not set up">{data.error}</Alert>
      )}
      {data?.configured && data.error && <Alert tone="critical" title="Could not reach the Control Hub">{data.error}</Alert>}

      {data && data.links.length > 0 && (
        <Card title="Connections made here">
          <div className="divide-y divide-grid">
            {data.links.map((l) => <LinkRow key={l.id} link={l} name={name} pending={data.pending ?? {}} mayControl={mayControl} onChanged={() => load(true)} />)}
          </div>
        </Card>
      )}

      {data?.configured && groups.map((g) => {
        const list = shown.filter((d) => d.kind === g.kind);
        if (!list.length) return null;
        return (
          <Card
            key={g.kind}
            title={`${g.title} (${list.filter((d) => d.online).length}/${list.length} online)`}
            action={g.kind === 'encoder' ? (
              <div className="flex gap-1">
                <Button variant={tiles ? 'ghost' : 'secondary'} onClick={() => setTileView(false)}>List</Button>
                <Button variant={tiles ? 'secondary' : 'ghost'} onClick={() => setTileView(true)}>Pictures</Button>
              </div>
            ) : undefined}
          >
            {g.kind === 'encoder' && tiles ? (
              <div className="grid gap-3 p-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                {list.map((d) => (
                  <button key={d.sn} className="rounded-md p-2 text-left ring-1 ring-hairline hover:bg-raised" onClick={() => setTileOpen(d.sn)}>
                    <Thumb sn={d.sn} online={d.online} className="aspect-video w-full" />
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">{d.name}</span>
                      <OnlineDot online={d.online} />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {d.online && <StatusLine status={status[d.sn]} />}
                      {(data.pending?.[d.sn] ?? 0) > 0 && <Badge tone="warning">{data.pending?.[d.sn]} waiting</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-muted">
                  <tr className="border-b border-grid">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-2 py-2 font-medium">Model</th>
                    <th className="px-2 py-2 font-medium">State</th>
                    <th className="hidden px-2 py-2 font-medium md:table-cell">IP</th>
                    <th className="hidden px-2 py-2 font-medium lg:table-cell">Firmware</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((d) => (
                    <Fragment key={d.sn}>
                      <tr className="border-b border-grid last:border-0">
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-3">
                            {d.kind === 'encoder' && <Thumb sn={d.sn} online={d.online} />}
                            <div>
                              <button className="text-left font-medium hover:underline" onClick={() => setOpen(open === d.sn ? null : d.sn)}>
                                {open === d.sn ? '▾' : '▸'} {d.name}
                              </button>
                              <div className="text-xs text-ink-muted">{d.sn}{d.upTime != null && d.online ? ` · up ${fmtUp(d.upTime)}` : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs text-ink-secondary">{d.model}</td>
                        <td className="px-2 py-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <OnlineDot online={d.online} />
                            {d.online && <StatusLine status={status[d.sn]} />}
                            {d.online && (d.warns & 1) !== 0 && <Badge tone="warning">No signal</Badge>}
                            {(data.pending?.[d.sn] ?? 0) > 0 && <Badge tone="warning">{data.pending?.[d.sn]} waiting</Badge>}
                          </div>
                        </td>
                        <td className="hidden px-2 py-2 text-xs text-ink-secondary md:table-cell">{d.ip || '—'}</td>
                        <td className="hidden px-2 py-2 text-xs text-ink-secondary lg:table-cell">{d.firmware}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {can('magewell:open') && data.canOpen && d.online && <OpenButton sn={d.sn} />}
                            {d.kind !== 'other' && (
                              <Button variant="ghost" onClick={() => setOpen(open === d.sn ? null : d.sn)}>{open === d.sn ? 'Close' : 'Manage'}</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {open === d.sn && d.kind !== 'other' && (
                        <tr className="border-b border-grid bg-plane/40">
                          <td colSpan={6} className="px-4 py-3">
                            {d.kind === 'encoder'
                                ? <EncoderPanel sn={d.sn} mayControl={mayControl} onConnect={(p) => setConnect({ ...p, encoderSn: d.sn })} />
                                : <DecoderPanel sn={d.sn} mayControl={mayControl} onConnect={(p) => setConnect({ ...p, decoderSn: d.sn })} />}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </Card>
        );
      })}

      {tileOpen && data && (() => {
        const d = data.devices.find((x) => x.sn === tileOpen);
        if (!d) return null;
        return (
          <Modal open onClose={() => setTileOpen(null)} title={d.name} width="max-w-4xl">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Thumb sn={d.sn} online={d.online} className="aspect-video w-full max-w-md" />
              <div className="space-y-1 text-xs text-ink-secondary">
                <div>{d.model} · {d.sn}</div>
                <OnlineDot online={d.online} />
                {can('magewell:open') && data.canOpen && d.online && <div><OpenButton sn={d.sn} /></div>}
              </div>
            </div>
            <EncoderPanel sn={d.sn} mayControl={mayControl} onConnect={(p) => { setTileOpen(null); setConnect({ ...p, encoderSn: d.sn }); }} />
          </Modal>
        );
      })()}

      {data?.configured && !data.error && data.devices.length === 0 && (
        <Card><Empty title="No devices">The Control Hub has no accepted devices.</Empty></Card>
      )}

      {showFavs && <FavoritesModal onClose={() => setShowFavs(false)} />}

      {connect && data && (
        <ConnectModal
          preset={connect}
          devices={data.devices}
          onClose={() => setConnect(null)}
          onDone={() => { setConnect(null); load(true); }}
        />
      )}
    </div>
  );
}

function OpenButton({ sn }: { sn: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      <Button
        variant="secondary"
        loading={busy}
        title={err ?? "The device's own web page, through the Control Hub"}
        onClick={async () => {
          // Opened first, then pointed at the link: a window opened after an await is a pop-up to the browser.
          const w = window.open('about:blank', '_blank');
          setBusy(true);
          try {
            const r = await api.post<{ url: string }>(`/magewell/${sn}/open`);
            if (w) w.location.href = r.url; else window.location.href = r.url;
            setErr(null);
          } catch (e) {
            w?.close();
            setErr(message(e));
          } finally { setBusy(false); }
        }}
      >
        Open ↗
      </Button>
      {err && <span className="self-center text-xs" style={{ color: 'var(--color-critical)' }}>{err}</span>}
    </>
  );
}

// ===========================================================================
// Connections
// ===========================================================================

const LINK_LABEL: Record<MwLink['kind'], string> = {
  'encoder-flow': 'Encoder → flow',
  'flow-decoder': 'Flow → decoder',
  bridge: 'Encoder → flow → decoder',
  direct: 'Encoder → decoder (direct)',
};

function LinkRow({ link: l, name, pending, mayControl, onChanged }: { link: MwLink; name: (sn: string | null) => string; pending: Record<string, number>; mayControl: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const d = l.detail as { listener?: string; publicHost?: string; port?: number; latency?: number; ingest?: string; egress?: string };
  const parts: React.ReactNode[] = [];
  if (l.encoderSn) parts.push(<span key="e"><b>{name(l.encoderSn)}</b>{l.serverName ? <span className="text-ink-muted"> ({l.serverName})</span> : <span className="text-ink-muted"> (destination gone)</span>}</span>);
  if (l.flowId) parts.push(<span key="f">flow <Link className="underline" to={`/flows/${l.flowId}`}>{l.flowName ?? l.flowId}</Link>{l.flowOutput ? ` · ${l.flowOutput}` : ''}</span>);
  if (l.kind === 'direct') parts.push(<span key="x" className="text-ink-secondary">{d.listener === 'encoder' ? 'encoder listens' : 'decoder listens'} on {d.publicHost}:{d.port}</span>);
  if (l.decoderSn) parts.push(<span key="d"><b>{name(l.decoderSn)}</b>{l.presetName ? <span className="text-ink-muted"> ({l.presetName})</span> : <span className="text-ink-muted"> (source gone)</span>}</span>);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
      <div className="min-w-0">
        <div className="text-xs text-ink-muted">{LINK_LABEL[l.kind]}{d.latency ? ` · ${d.latency} ms` : ''} · {formatRelative(l.createdAt)}{l.createdBy ? ` by ${l.createdBy}` : ''}</div>
        <div className="flex flex-wrap items-center gap-x-2">{parts.map((p, i) => <Fragment key={i}>{i > 0 && <span className="text-ink-muted">→</span>}{p}</Fragment>)}</div>
        {((l.detail.waitingFor as string[] | undefined) ?? []).filter((sn) => pending[sn]).map((sn) => <div key={sn} className="text-xs" style={{ color: 'var(--color-warning)' }}>Waiting for {name(sn)} to come online</div>)}
        {notes?.map((n) => <div key={n} className="text-xs text-ink-muted">{n}</div>)}
        {err && <div className="text-xs" style={{ color: 'var(--color-critical)' }}>{err}</div>}
      </div>
      {mayControl && (
        <Button
          variant="ghost" loading={busy}
          title="Remove the encoder's destination, the decoder's source and any flow output made for this"
          onClick={async () => {
            setBusy(true);
            try {
              const r = await api.delete<{ notes: string[] }>(`/magewell/links/${l.id}`);
              if (r.notes.length) { setNotes(r.notes); setTimeout(onChanged, 4000); } else onChanged();
            } catch (e) { setErr(message(e)); } finally { setBusy(false); }
          }}
        >
          Take down
        </Button>
      )}
    </div>
  );
}

interface ConnectPreset {
  kind: MwLink['kind'];
  encoderSn: string;
  decoderSn: string;
}

function useFlows() {
  const [flows, setFlows] = useState<FlowSummary[] | null>(null);
  useEffect(() => { void api.get<{ flows: FlowSummary[] }>('/flows').then((r) => setFlows(r.flows)).catch(() => setFlows([])); }, []);
  return flows;
}

function ConnectModal({ preset, devices, onClose, onDone }: { preset: Partial<ConnectPreset>; devices: MwDevice[]; onClose: () => void; onDone: () => void }) {
  const flows = useFlows();
  const [kind, setKind] = useState<MwLink['kind']>(preset.kind ?? (preset.decoderSn && !preset.encoderSn ? 'flow-decoder' : 'encoder-flow'));
  const [encoderSn, setEncoderSn] = useState(preset.encoderSn ?? '');
  const [decoderSn, setDecoderSn] = useState(preset.decoderSn ?? '');
  const [flowMode, setFlowMode] = useState<'existing' | 'new'>('existing');
  const [flowId, setFlowId] = useState('');
  const [newFlowName, setNewFlowName] = useState('');
  const [startFlow, setStartFlow] = useState(false);
  const [outputs, setOutputs] = useState<FlowOutput[] | null>(null);
  const [outputMode, setOutputMode] = useState<'new' | 'existing'>('new');
  const [output, setOutput] = useState('');
  const [newOutputPort, setNewOutputPort] = useState('');
  const [listener, setListener] = useState<'encoder' | 'decoder'>('decoder');
  const [publicHost, setPublicHost] = useState('');
  const [port, setPort] = useState('5000');
  const [latency, setLatency] = useState('200');
  const [passphrase, setPassphrase] = useState('');
  const [streamIndex, setStreamIndex] = useState('0');
  const [enable, setEnable] = useState(true);
  const [select, setSelect] = useState(true);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const encoders = devices.filter((d) => d.kind === 'encoder');
  const decoders = devices.filter((d) => d.kind === 'decoder');
  const usesEncoder = kind !== 'flow-decoder';
  const usesDecoder = kind !== 'encoder-flow';
  const usesFlow = kind !== 'direct';
  const pullsFromFlow = kind === 'flow-decoder' || kind === 'bridge';
  // A decoder pulling from a flow can make a flow only when an encoder is being connected to it in the same go.
  const canMakeFlow = kind !== 'flow-decoder';

  useEffect(() => {
    setOutputs(null);
    if (!flowId || flowMode !== 'existing') return;
    void api.get<{ flow: { outputs: FlowOutput[] } }>(`/flows/${flowId}`).then((r) => setOutputs(r.flow.outputs)).catch(() => setOutputs([]));
  }, [flowId, flowMode]);

  useEffect(() => { if (!canMakeFlow) setFlowMode('existing'); }, [canMakeFlow]);

  const pullOutputs = (outputs ?? []).filter((o) => o.protocol === 'srt-listener');
  const liveFlows = (flows ?? []).filter((f) => !['deleted', 'deleting', 'failed'].includes(f.state));
  const chosenFlow = liveFlows.find((f) => f.id === flowId);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = { kind, latency: Number(latency) || undefined, label: label.trim() || undefined };
      if (usesEncoder) { body.encoderSn = encoderSn; body.enable = enable; body.streamIndex = Number(streamIndex); }
      if (usesDecoder) { body.decoderSn = decoderSn; body.select = select; }
      if (usesFlow) {
        if (flowMode === 'new') body.newFlow = { name: newFlowName.trim(), start: startFlow };
        else body.flowId = flowId;
        if (pullsFromFlow) {
          if (flowMode === 'existing' && outputMode === 'existing') body.output = output;
          else body.newOutput = newOutputPort ? { port: Number(newOutputPort) } : {};
        }
      } else {
        Object.assign(body, { listener, publicHost: publicHost.trim(), port: Number(port), passphrase: passphrase || undefined });
      }
      await api.post('/magewell/links', body);
      onDone();
    } catch (e) {
      setErr(message(e));
    } finally {
      setBusy(false);
    }
  }

  const ready =
    (!usesEncoder || encoderSn) && (!usesDecoder || decoderSn) &&
    (!usesFlow || (flowMode === 'new' ? /^[A-Za-z0-9][A-Za-z0-9_-]{1,59}$/.test(newFlowName.trim()) : flowId)) &&
    (!pullsFromFlow || flowMode === 'new' || outputMode === 'new' || output) &&
    (usesFlow || (publicHost.trim() && Number(port) > 0));

  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      title="Connect"
      width="max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!ready} onClick={submit}>Connect</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What">
          <Select value={kind} onChange={(e) => setKind(e.target.value as MwLink['kind'])}>
            <option value="encoder-flow">Encoder → a flow</option>
            <option value="flow-decoder">A flow → decoder</option>
            <option value="bridge">Encoder → decoder, through a flow</option>
            <option value="direct">Encoder → decoder, direct (one end listens on a public address)</option>
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          {usesEncoder && (
            <Field label="Encoder">
              <Select value={encoderSn} onChange={(e) => setEncoderSn(e.target.value)}>
                <option value="">Choose…</option>
                {encoders.map((d) => <option key={d.sn} value={d.sn}>{d.name}{d.online ? '' : ' (offline: set up when it is back)'}</option>)}
              </Select>
            </Field>
          )}
          {usesDecoder && (
            <Field label="Decoder">
              <Select value={decoderSn} onChange={(e) => setDecoderSn(e.target.value)}>
                <option value="">Choose…</option>
                {decoders.map((d) => <option key={d.sn} value={d.sn}>{d.name}{d.online ? '' : ' (offline: set up when it is back)'}</option>)}
              </Select>
            </Field>
          )}
        </div>

        {usesFlow && (
          <div className="space-y-3 rounded-md p-3 ring-1 ring-hairline">
            {canMakeFlow && (
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1.5"><input type="radio" checked={flowMode === 'existing'} onChange={() => setFlowMode('existing')} /> A flow on the hub</label>
                <label className="flex items-center gap-1.5"><input type="radio" checked={flowMode === 'new'} onChange={() => setFlowMode('new')} /> Make a new flow</label>
              </div>
            )}
            {flowMode === 'existing' ? (
              <Field
                label="Flow"
                hint={chosenFlow && usesEncoder
                  ? chosenFlow.source.protocol === 'srt-listener'
                    ? `The encoder calls the flow's SRT listener on port ${chosenFlow.source.port}.`
                    : `Its source is ${chosenFlow.source.protocol}: change it to an SRT listener on the flow's page first, or make a new flow.`
                  : undefined}
              >
                <Select value={flowId} onChange={(e) => setFlowId(e.target.value)}>
                  <option value="">{flows ? 'Choose…' : 'Loading…'}</option>
                  {liveFlows.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.state})</option>)}
                </Select>
              </Field>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="New flow's name" hint="Its source is an SRT listener on port 5000, open to anyone.">
                  <Input value={newFlowName} onChange={(e) => setNewFlowName(e.target.value.toUpperCase())} placeholder="NZC_CAM1" />
                </Field>
                <label className="flex items-center gap-2 self-center text-sm">
                  <input type="checkbox" checked={startFlow} onChange={(e) => setStartFlow(e.target.checked)} /> Start it now (about US$0.27 an hour)
                </label>
              </div>
            )}
            {pullsFromFlow && (
              <div className="space-y-2">
                {flowMode === 'existing' && (
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-1.5"><input type="radio" checked={outputMode === 'new'} onChange={() => setOutputMode('new')} /> Add an output for the decoder</label>
                    <label className="flex items-center gap-1.5"><input type="radio" checked={outputMode === 'existing'} onChange={() => setOutputMode('existing')} /> Use an output already there</label>
                  </div>
                )}
                {flowMode === 'existing' && outputMode === 'existing' ? (
                  <Field label="Output" hint="SRT listener outputs only: the decoder calls in to it.">
                    <Select value={output} onChange={(e) => setOutput(e.target.value)}>
                      <option value="">{outputs ? (pullOutputs.length ? 'Choose…' : 'None on this flow') : flowId ? 'Loading…' : 'Choose a flow first'}</option>
                      {pullOutputs.map((o) => <option key={o.name} value={o.name}>{o.name} :{o.port}{o.cidr && o.cidr !== '0.0.0.0/0' ? ` (only ${o.cidr})` : ''}</option>)}
                    </Select>
                  </Field>
                ) : (
                  <Field label="Output port" hint="An SRT listener on the flow, open to anyone. Empty: the next free port from 7000.">
                    <Input value={newOutputPort} onChange={(e) => setNewOutputPort(e.target.value.replace(/\D/g, ''))} placeholder="7000" />
                  </Field>
                )}
              </div>
            )}
          </div>
        )}

        {!usesFlow && (
          <div className="space-y-3 rounded-md p-3 ring-1 ring-hairline">
            <Field label="Which end listens">
              <Select value={listener} onChange={(e) => setListener(e.target.value as 'encoder' | 'decoder')}>
                <option value="decoder">The decoder listens; the encoder calls it</option>
                <option value="encoder">The encoder listens; the decoder calls it</option>
              </Select>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={`The ${listener}'s public address`} hint="The listening site's public IP or host name. That site must forward this UDP port to the device.">
                <Input value={publicHost} onChange={(e) => setPublicHost(e.target.value)} placeholder="203.0.113.10" />
              </Field>
              <Field label="UDP port" hint={`The ${listener} listens on this port too.`}>
                <Input value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} />
              </Field>
            </div>
            <Field label="Passphrase" hint="Optional. 10 to 79 characters; set on both ends.">
              <Input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} type="password" autoComplete="new-password" />
            </Field>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Latency (ms)" hint="20 to 8000">
            <Input value={latency} onChange={(e) => setLatency(e.target.value.replace(/\D/g, ''))} />
          </Field>
          {usesEncoder && (
            <Field label="Encoder stream">
              <Select value={streamIndex} onChange={(e) => setStreamIndex(e.target.value)}>
                <option value="0">Main</option>
                <option value="1">Sub</option>
              </Select>
            </Field>
          )}
          <Field label="Name on the devices" hint="Empty: named after the flow or far end.">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={32} />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          {usesEncoder && <label className="flex items-center gap-1.5"><input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} /> Start sending now</label>}
          {usesDecoder && <label className="flex items-center gap-1.5"><input type="checkbox" checked={select} onChange={(e) => setSelect(e.target.checked)} /> Switch the decoder to it now</label>}
        </div>

        {busy && usesFlow && flowMode === 'new' && <Alert>Making the flow. This takes about a minute.</Alert>}
        {err && <Alert tone="critical">{err}</Alert>}
      </div>
    </Modal>
  );
}

// ===========================================================================
// Encoder
// ===========================================================================

// ===========================================================================
// Favourites
// ===========================================================================

interface Favorite {
  id: string; kind: 'source' | 'destination'; label: string; protocol: 'srt-caller' | 'srt-listener' | 'rtmp' | 'url';
  host?: string | null; port?: number | null; latency?: number | null; streamId?: string | null; url?: string | null;
}

type FavoriteFields = Pick<Favorite, 'protocol' | 'host' | 'port' | 'latency' | 'streamId' | 'url'>;

function useFavorites(kind?: Favorite['kind']) {
  const [all, setAll] = useState<Favorite[]>([]);
  const reload = useCallback(() => {
    void api.get<{ favorites: Favorite[] }>('/magewell/favorites').then((r) => setAll(r.favorites)).catch(() => undefined);
  }, []);
  useEffect(reload, [reload]);
  return { items: kind ? all.filter((f) => f.kind === kind) : all, reload };
}

const favoriteWhere = (f: FavoriteFields) =>
  f.protocol === 'srt-caller' ? `${f.host}:${f.port}` : f.protocol === 'srt-listener' ? `listen :${f.port}` : f.url ?? '';

function FavoritePicker({ items, onPick }: { items: Favorite[]; onPick: (f: Favorite) => void }) {
  if (!items.length) return null;
  return (
    <Field label="From favourites" hint="Fills the fields below; change anything before saving.">
      <Select value="" onChange={(e) => { const f = items.find((x) => x.id === e.target.value); if (f) onPick(f); }}>
        <option value="">Choose a favourite…</option>
        {items.map((f) => <option key={f.id} value={f.id}>{f.label}: {favoriteWhere(f)}</option>)}
      </Select>
    </Field>
  );
}

/** Keep what is in the form as a favourite, named after it. No passphrase or key is kept. */
function SaveFavorite({ kind, label, fields, onSaved }: { kind: Favorite['kind']; label: string; fields: FavoriteFields; onSaved: () => void }) {
  const [state, setState] = useState<'idle' | 'busy' | 'saved' | string>('idle');
  const { can } = useAuth();
  if (!can('magewell:control')) return null;
  return (
    <span className="mr-auto flex items-center gap-2">
      <Button
        variant="ghost"
        loading={state === 'busy'}
        disabled={!label.trim()}
        title="Keep these settings (not the passphrase or key) to fill in next time"
        onClick={async () => {
          setState('busy');
          try {
            await api.post('/magewell/favorites', { kind, label: label.trim(), ...fields });
            setState('saved');
            onSaved();
          } catch (e) { setState(message(e)); }
        }}
      >
        ☆ Save as favourite
      </Button>
      {state === 'saved' && <span className="text-xs text-ink-muted">Saved</span>}
      {!['idle', 'busy', 'saved'].includes(state) && <span className="text-xs" style={{ color: 'var(--color-critical)' }}>{state}</span>}
    </span>
  );
}

const EMPTY_FAVORITE: Omit<Favorite, 'id'> = { kind: 'source', label: '', protocol: 'srt-caller', host: '', port: null, latency: 200, streamId: '', url: '' };

function FavoritesModal({ onClose }: { onClose: () => void }) {
  const { can } = useAuth();
  const { items, reload } = useFavorites();
  const [edit, setEdit] = useState<(Omit<Favorite, 'id'> & { id?: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mayControl = can('magewell:control');

  async function save() {
    if (!edit) return;
    setBusy(true);
    setErr(null);
    const body = { ...edit, port: edit.port ? Number(edit.port) : null, latency: edit.latency ? Number(edit.latency) : null };
    try {
      if (edit.id) await api.patch(`/magewell/favorites/${edit.id}`, body);
      else await api.post('/magewell/favorites', body);
      setEdit(null);
      reload();
    } catch (e) { setErr(message(e)); } finally { setBusy(false); }
  }

  const set = (patch: Partial<Favorite>) => setEdit((e) => (e ? { ...e, ...patch } : e));
  const section = (kind: Favorite['kind'], title: string) => (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-ink-secondary">{title}</h3>
        {mayControl && <Button variant="ghost" onClick={() => setEdit({ ...EMPTY_FAVORITE, kind })}>+ Add</Button>}
      </div>
      {items.filter((f) => f.kind === kind).length === 0
        ? <p className="text-xs text-ink-muted">None yet.</p>
        : items.filter((f) => f.kind === kind).map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-2 border-b border-grid py-1.5 text-sm last:border-0">
            <div className="min-w-0"><b>{f.label}</b> <span className="text-xs text-ink-secondary">{f.protocol} {favoriteWhere(f)}{f.latency ? ` · ${f.latency} ms` : ''}</span></div>
            {mayControl && (
              <div className="flex gap-1">
                <Button variant="ghost" onClick={() => setEdit({ ...f })}>Edit</Button>
                <Button variant="ghost" onClick={async () => { await api.delete(`/magewell/favorites/${f.id}`).catch(() => undefined); reload(); }}>Delete</Button>
              </div>
            )}
          </div>
        ))}
    </div>
  );

  return (
    <Modal open onClose={onClose} title="Favourites" width="max-w-2xl">
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">Offered under "From favourites" when setting a decoder's source or an encoder's destination. Shared by everyone. Passphrases and stream keys are not kept.</p>
        {section('source', 'Sources (what a decoder plays)')}
        {section('destination', 'Destinations (where an encoder sends)')}
        {edit && (
          <div className="space-y-3 rounded-md p-3 ring-1 ring-hairline">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Label"><Input value={edit.label} onChange={(e) => set({ label: e.target.value })} maxLength={60} /></Field>
              <Field label="For">
                <Select value={edit.kind} onChange={(e) => set({ kind: e.target.value as Favorite['kind'] })}>
                  <option value="source">Decoder source</option>
                  <option value="destination">Encoder destination</option>
                </Select>
              </Field>
              <Field label="Type">
                <Select value={edit.protocol} onChange={(e) => set({ protocol: e.target.value as Favorite['protocol'] })}>
                  <option value="srt-caller">SRT caller</option>
                  <option value="srt-listener">SRT listener</option>
                  <option value="rtmp">RTMP</option>
                  {edit.kind === 'source' && <option value="url">Any address</option>}
                </Select>
              </Field>
            </div>
            {edit.protocol === 'rtmp' || edit.protocol === 'url' ? (
              <Field label="Address"><Input value={edit.url ?? ''} onChange={(e) => set({ url: e.target.value })} /></Field>
            ) : (
              <div className="grid gap-3 sm:grid-cols-4">
                {edit.protocol === 'srt-caller' && <Field label="Host"><Input value={edit.host ?? ''} onChange={(e) => set({ host: e.target.value })} placeholder="studio.engagevideo.co.nz" /></Field>}
                <Field label="Port"><Input value={edit.port ?? ''} onChange={(e) => set({ port: Number(e.target.value.replace(/\D/g, '')) || null })} /></Field>
                <Field label="Latency (ms)"><Input value={edit.latency ?? ''} onChange={(e) => set({ latency: Number(e.target.value.replace(/\D/g, '')) || null })} /></Field>
                {edit.protocol === 'srt-caller' && <Field label="Stream ID"><Input value={edit.streamId ?? ''} onChange={(e) => set({ streamId: e.target.value })} /></Field>}
              </div>
            )}
            {err && <Alert tone="critical">{err}</Alert>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</Button>
              <Button variant="primary" loading={busy} disabled={!edit.label.trim()} onClick={save}>{edit.id ? 'Save' : 'Add'}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** A device that is offline is shown as last read; changes to it wait. */
function OfflineNote({ read, kind }: { read: DeviceRead<unknown>; kind: 'encoder' | 'decoder' }) {
  if (!read.stale) return null;
  return (
    <Alert tone="warning" title={`This ${kind} is offline`}>
      {read.detail
        ? <>Shown as it was {formatRelative(read.detailAt ?? null)}. </>
        : <>The hub has not read it yet, so nothing it has can be shown. </>}
      Changes made here wait and are made when it next comes online.
    </Alert>
  );
}

/** Changes waiting for the device to come online, in the order they will be made. */
function PendingList({ items, mayControl, onChanged }: { items: Pending[]; mayControl: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!items.length) return null;
  return (
    <div className="rounded-md ring-1 ring-hairline">
      <div className="border-b border-grid px-3 py-1.5 text-xs font-medium text-ink-secondary">Waiting for the device ({items.length}), made in this order</div>
      {items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-grid px-3 py-1.5 text-sm last:border-0">
          <div className="min-w-0">
            <span className="text-ink-secondary">{OP_LABEL[p.op] ?? p.op}:</span> {p.what}
            <div className="text-[11px] text-ink-muted">{formatRelative(p.createdAt)}{p.createdBy ? ` by ${p.createdBy}` : ''}</div>
            {p.error && <div className="text-[11px]" style={{ color: 'var(--color-critical)' }}>Tried {p.tries} time{p.tries === 1 ? '' : 's'}: {p.error}</div>}
          </div>
          {mayControl && (
            <Button variant="ghost" loading={busy === p.id} onClick={async () => {
              setBusy(p.id);
              try { await api.delete(`/magewell/pending/${p.id}`); } finally { setBusy(null); onChanged(); }
            }}>Cancel</Button>
          )}
        </div>
      ))}
    </div>
  );
}

function EncoderPanel({ sn, mayControl, onConnect }: { sn: string; mayControl: boolean; onConnect: (p: Partial<ConnectPreset>) => void }) {
  const [read, setRead] = useState<DeviceRead<EncoderDetail> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<Server | 'new' | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const detail = read?.detail ?? null;

  const load = useCallback(() => {
    api.get<DeviceRead<EncoderDetail>>(`/magewell/${sn}`)
      .then((r) => { setRead(r); setErr(r.error ?? null); })
      .catch((e) => setErr(message(e)));
  }, [sn]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  async function act(id: number, fn: () => Promise<unknown>) {
    setBusy(id);
    try { await fn(); load(); } catch (e) { setErr(message(e)); } finally { setBusy(null); }
  }

  if (!read && !err) return <Spinner />;

  return (
    <div className="space-y-3">
      {err && <Alert tone="critical">{err}</Alert>}
      {read && <OfflineNote read={read} kind="encoder" />}
      {read && <PendingList items={read.pending} mayControl={mayControl} onChanged={load} />}
      {!detail && read?.stale && mayControl && (
        <div className="flex gap-1">
          <Button variant="secondary" onClick={() => onConnect({ kind: 'encoder-flow' })}>Send to a flow…</Button>
          <Button variant="secondary" onClick={() => setEdit('new')}>+ Destination</Button>
        </div>
      )}
      {detail && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>Input: <b className={detail.hasSignal ? 'text-ink' : ''}>{detail.input}</b></span>
              {detail.main && <span>Main: {detail.main.codec} {detail.main.width}x{detail.main.height}{detail.main.fps ? ` ${detail.main.fps}` : ''} {detail.main.kbps} kb/s</span>}
              {detail.sub && <span>Sub: {detail.sub.codec} {detail.sub.width}x{detail.sub.height} {detail.sub.kbps} kb/s</span>}
            </div>
            {mayControl && (
              <div className="flex gap-1">
                <Button variant="secondary" onClick={() => onConnect({ kind: 'encoder-flow' })}>Send to a flow…</Button>
                <Button variant="secondary" onClick={() => onConnect({ kind: 'bridge' })}>Send to a decoder…</Button>
                <Button variant="secondary" onClick={() => setEdit('new')}>+ Destination</Button>
              </div>
            )}
          </div>

          {detail.servers.length === 0 ? (
            <p className="text-sm text-ink-muted">No destinations on this encoder.</p>
          ) : (
            <div className="overflow-x-auto rounded-md ring-1 ring-hairline">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-muted">
                  <tr className="border-b border-grid">
                    <th className="px-3 py-1.5 font-medium">Destination</th>
                    <th className="px-2 py-1.5 font-medium">Type</th>
                    <th className="px-2 py-1.5 font-medium">Where</th>
                    <th className="px-2 py-1.5 font-medium">Sending</th>
                    <th className="px-3 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {detail.servers.map((s) => (
                    <tr key={s.id} className="border-b border-grid last:border-0">
                      <td className="px-3 py-1.5">{s.name}<div className="text-[11px] text-ink-muted">{s.streamIndex === 1 ? 'Sub stream' : 'Main stream'}</div></td>
                      <td className="px-2 py-1.5 text-xs">{s.typeName}{s.encrypted && ' 🔒'}</td>
                      <td className="px-2 py-1.5 text-xs text-ink-secondary">
                        {s.protocol === 'srt-caller' && `${s.host}:${s.port}`}
                        {s.protocol === 'srt-listener' && `listens on :${s.port}`}
                        {s.protocol === 'rtmp' && <span className="break-all">{s.url}{s.key ? ` / ${s.key}` : ''}</span>}
                        {s.latency ? <span className="text-ink-muted"> · {s.latency} ms</span> : null}
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        <span className="flex flex-wrap items-center gap-1">
                          {s.state ? <StateBadge state={s.state} /> : <Badge>{s.enabled ? 'On' : 'Off'}</Badge>}
                          {s.state?.tone === 'good' && s.live?.kbps ? <span className="text-ink-secondary">{s.live.kbps} kb/s</span> : null}
                          {s.enabled && s.live?.rttMs != null && <span className="text-ink-muted">rtt {s.live.rttMs} ms</span>}
                          {s.enabled && s.live && s.state?.tone === 'good' && s.live.runMs > 0 && <span className="text-ink-muted">for {fmtUp(Math.round(s.live.runMs / 1000))}</span>}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {mayControl && (
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" loading={busy === s.id} onClick={() => act(s.id, () => api.post(`/magewell/${sn}/destinations/${s.id}/enable`, { enable: !s.enabled }))}>
                              {s.enabled ? 'Stop' : 'Start'}
                            </Button>
                            {s.protocol !== 'other' && <Button variant="ghost" onClick={() => setEdit(s)}>Edit</Button>}
                            <Button variant="ghost" onClick={() => { if (window.confirm(`Delete ${s.name} from this encoder?`)) void act(s.id, () => api.delete(`/magewell/${sn}/destinations/${s.id}`)); }}>Delete</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {edit && <DestinationModal sn={sn} server={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function DestinationModal({ sn, server, onClose, onDone }: { sn: string; server: Server | null; onClose: () => void; onDone: () => void }) {
  const [protocol, setProtocol] = useState<'srt-caller' | 'srt-listener' | 'rtmp'>(server && server.protocol !== 'other' ? server.protocol : 'srt-caller');
  const [name, setName] = useState(server?.name ?? '');
  const [host, setHost] = useState(server?.host ?? '');
  const [port, setPort] = useState(String(server?.port ?? ''));
  const [latency, setLatency] = useState(String(server?.latency ?? 200));
  const [streamId, setStreamId] = useState(server?.streamId ?? '');
  const [passphrase, setPassphrase] = useState('');
  const [url, setUrl] = useState(server?.url ?? '');
  const [key, setKey] = useState('');
  const [streamIndex, setStreamIndex] = useState(String(server?.streamIndex ?? 0));
  const [enable, setEnable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const favs = useFavorites('destination');

  /** A favourite fills the form; everything stays editable. */
  function applyFavorite(f: Favorite) {
    if (f.protocol !== 'url') setProtocol(f.protocol);
    setHost(f.host ?? '');
    setPort(f.port != null ? String(f.port) : '');
    if (f.latency != null) setLatency(String(f.latency));
    setStreamId(f.streamId ?? '');
    setUrl(f.url ?? '');
    if (!server) setName(f.label.slice(0, 32));
  }
  const current = { protocol, host, port: Number(port) || null, latency: Number(latency) || null, streamId, url };

  async function save() {
    setBusy(true);
    setErr(null);
    const body: Record<string, unknown> = { protocol, name: name.trim(), streamIndex: Number(streamIndex) };
    if (protocol === 'rtmp') { body.url = url.trim(); if (key || !server) body.key = key; }
    else {
      body.port = Number(port);
      body.latency = Number(latency) || undefined;
      if (protocol === 'srt-caller') { body.host = host.trim(); body.streamId = streamId; }
      // Left empty on an edit: keep the passphrase the encoder has.
      if (passphrase || !server) body.passphrase = passphrase;
    }
    try {
      if (server) await api.patch(`/magewell/${sn}/destinations/${server.id}`, body);
      else await api.post(`/magewell/${sn}/destinations`, { ...body, enable });
      onDone();
    } catch (e) { setErr(message(e)); } finally { setBusy(false); }
  }

  return (
    <Modal
      open onClose={onClose} title={server ? `Edit ${server.name}` : 'New destination'}
      footer={<>
        <SaveFavorite kind="destination" label={name} fields={current} onSaved={favs.reload} />
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={save}>Save</Button>
      </>}
    >
      <div className="space-y-3">
        <FavoritePicker items={favs.items} onPick={applyFavorite} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            <Select value={protocol} onChange={(e) => setProtocol(e.target.value as typeof protocol)}>
              <option value="srt-caller">SRT caller (sends to an address)</option>
              <option value="srt-listener">SRT listener (waits to be called)</option>
              <option value="rtmp">RTMP</option>
            </Select>
          </Field>
          <Field label="Name" hint="Up to 32 characters.">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={32} />
          </Field>
        </div>
        {protocol === 'rtmp' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Server URL"><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="rtmp://a.rtmp.youtube.com/live2" /></Field>
            <Field label="Stream key" hint={server ? 'Empty: keep the one it has.' : undefined}><Input value={key} onChange={(e) => setKey(e.target.value)} type="password" autoComplete="off" /></Field>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {protocol === 'srt-caller' && <Field label="Host"><Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="203.0.113.10" /></Field>}
              <Field label={protocol === 'srt-caller' ? 'Port' : 'Listen on port'}><Input value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} /></Field>
              <Field label="Latency (ms)"><Input value={latency} onChange={(e) => setLatency(e.target.value.replace(/\D/g, ''))} /></Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {protocol === 'srt-caller' && <Field label="Stream ID" hint="Optional."><Input value={streamId} onChange={(e) => setStreamId(e.target.value)} /></Field>}
              <Field label="Passphrase" hint={server ? 'Empty: keep what it has.' : 'Optional, 10 to 79 characters.'}><Input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} type="password" autoComplete="new-password" /></Field>
            </div>
          </>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Stream">
            <Select value={streamIndex} onChange={(e) => setStreamIndex(e.target.value)}>
              <option value="0">Main</option>
              <option value="1">Sub</option>
            </Select>
          </Field>
          {!server && <label className="flex items-center gap-2 self-center text-sm"><input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} /> Start sending now</label>}
        </div>
        {err && <Alert tone="critical">{err}</Alert>}
      </div>
    </Modal>
  );
}

// ===========================================================================
// Decoder
// ===========================================================================

function DecoderPanel({ sn, mayControl, onConnect }: { sn: string; mayControl: boolean; onConnect: (p: Partial<ConnectPreset>) => void }) {
  const [read, setRead] = useState<DeviceRead<DecoderDetail> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<SourceRow | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const detail = read?.detail ?? null;

  const load = useCallback(() => {
    api.get<DeviceRead<DecoderDetail>>(`/magewell/${sn}`)
      .then((r) => { setRead(r); setErr(r.error ?? null); })
      .catch((e) => setErr(message(e)));
  }, [sn]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  async function act(name: string, fn: () => Promise<unknown>) {
    setBusy(name);
    try { await fn(); load(); } catch (e) { setErr(message(e)); } finally { setBusy(null); }
  }

  if (!read && !err) return <Spinner />;
  const enc = (n: string) => encodeURIComponent(n);

  return (
    <div className="space-y-3">
      {err && <Alert tone="critical">{err}</Alert>}
      {read && <OfflineNote read={read} kind="decoder" />}
      {read && <PendingList items={read.pending} mayControl={mayControl} onChanged={load} />}
      {!detail && read?.stale && mayControl && (
        <div className="flex gap-1">
          <Button variant="secondary" onClick={() => onConnect({ kind: 'flow-decoder' })}>Pull from a flow…</Button>
          <Button variant="secondary" onClick={() => setEdit('new')}>+ Source</Button>
        </div>
      )}
      {detail && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>Playing: <b className="text-ink">{detail.current ?? 'nothing'}</b>{detail.currentIsNdi ? ' (NDI)' : ''}</span>
              {detail.state
                ? <StateBadge state={detail.state} />
                : detail.connected != null && <span>{detail.connected ? <Badge tone="good">Receiving</Badge> : <Badge tone="warning">Not receiving</Badge>}</span>}
              {detail.connected && detail.video && <span>{detail.video}{detail.kbps ? ` · ${detail.kbps} kb/s` : ''}</span>}
              {detail.connected && (detail.droppedVideo || detail.droppedAudio) ? <span>dropped {detail.droppedVideo ?? 0} video / {detail.droppedAudio ?? 0} audio frames</span> : null}
              {detail.connected && detail.jitterMs ? <span>jitter {detail.jitterMs} ms</span> : null}
            </div>
            {mayControl && (
              <div className="flex gap-1">
                <Button variant="secondary" onClick={() => onConnect({ kind: 'flow-decoder' })}>Pull from a flow…</Button>
                <Button variant="secondary" onClick={() => onConnect({ kind: 'bridge' })}>From an encoder…</Button>
                <Button variant="secondary" onClick={() => setEdit('new')}>+ Source</Button>
              </div>
            )}
          </div>

          {detail.sources.length === 0 ? (
            <p className="text-sm text-ink-muted">No sources on this decoder.</p>
          ) : (
            <div className="overflow-x-auto rounded-md ring-1 ring-hairline">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-muted">
                  <tr className="border-b border-grid">
                    <th className="px-3 py-1.5 font-medium">Source</th>
                    <th className="px-2 py-1.5 font-medium">Type</th>
                    <th className="px-2 py-1.5 font-medium">Where</th>
                    <th className="px-3 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {detail.sources.map((s) => (
                    <tr key={s.name} className="border-b border-grid last:border-0">
                      <td className="px-3 py-1.5">
                        <span className="flex items-center gap-1.5">{s.name}{s.active && <Badge tone="info">Playing</Badge>}</span>
                      </td>
                      <td className="px-2 py-1.5 text-xs">{s.protocol}{s.encrypted && ' 🔒'}</td>
                      <td className="px-2 py-1.5 text-xs text-ink-secondary">
                        {s.protocol === 'srt-caller' ? `${s.host}:${s.port}` : s.protocol === 'srt-listener' ? `listens on :${s.port}` : <span className="break-all">{s.url}</span>}
                        {s.latency ? <span className="text-ink-muted"> · {s.latency} ms</span> : null}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {mayControl && (
                          <div className="flex justify-end gap-1">
                            {!s.active && <Button variant="secondary" loading={busy === s.name} onClick={() => act(s.name, () => api.post(`/magewell/${sn}/sources/${enc(s.name)}/select`))}>Play</Button>}
                            <Button variant="ghost" onClick={() => setEdit(s)}>Edit</Button>
                            <Button variant="ghost" onClick={() => { if (window.confirm(`Delete ${s.name} from this decoder?`)) void act(s.name, () => api.delete(`/magewell/${sn}/sources/${enc(s.name)}`)); }}>Delete</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {edit && <SourceModal sn={sn} source={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function SourceModal({ sn, source, onClose, onDone }: { sn: string; source: SourceRow | null; onClose: () => void; onDone: () => void }) {
  const known = source && ['srt-caller', 'srt-listener', 'rtmp'].includes(source.protocol) ? source.protocol as 'srt-caller' | 'srt-listener' | 'rtmp' : source ? 'url' : 'srt-caller';
  const [protocol, setProtocol] = useState<'srt-caller' | 'srt-listener' | 'rtmp' | 'url'>(known);
  const [name, setName] = useState(source?.name ?? '');
  const [host, setHost] = useState(source?.host ?? '');
  const [port, setPort] = useState(String(source?.port ?? ''));
  const [latency, setLatency] = useState(String(source?.latency ?? 200));
  const [streamId, setStreamId] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [url, setUrl] = useState(source && (known === 'rtmp' || known === 'url') && !source.encrypted ? source.url : '');
  const [select, setSelect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const favs = useFavorites('source');

  /** A favourite fills the form; everything stays editable. */
  function applyFavorite(f: Favorite) {
    setProtocol(f.protocol);
    setHost(f.host ?? '');
    setPort(f.port != null ? String(f.port) : '');
    if (f.latency != null) setLatency(String(f.latency));
    setStreamId(f.streamId ?? '');
    setUrl(f.url ?? '');
    if (!source) setName(f.label);
  }
  const current = { protocol, host, port: Number(port) || null, latency: Number(latency) || null, streamId, url };

  async function save() {
    setBusy(true);
    setErr(null);
    const body: Record<string, unknown> = { protocol, name: name.trim() };
    if (protocol === 'rtmp' || protocol === 'url') body.url = url.trim();
    else {
      body.port = Number(port);
      body.latency = Number(latency) || undefined;
      if (protocol === 'srt-caller') { body.host = host.trim(); if (streamId) body.streamId = streamId; }
      if (passphrase || !source) body.passphrase = passphrase;
    }
    try {
      if (source) await api.patch(`/magewell/${sn}/sources/${encodeURIComponent(source.name)}`, body);
      else await api.post(`/magewell/${sn}/sources`, { ...body, select });
      onDone();
    } catch (e) { setErr(message(e)); } finally { setBusy(false); }
  }

  return (
    <Modal
      open onClose={onClose} title={source ? `Edit ${source.name}` : 'New source'}
      footer={<>
        <SaveFavorite kind="source" label={name} fields={current} onSaved={favs.reload} />
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={save}>Save</Button>
      </>}
    >
      <div className="space-y-3">
        <FavoritePicker items={favs.items} onPick={applyFavorite} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            <Select value={protocol} onChange={(e) => setProtocol(e.target.value as typeof protocol)}>
              <option value="srt-caller">SRT caller (calls an address)</option>
              <option value="srt-listener">SRT listener (waits to be sent to)</option>
              <option value="rtmp">RTMP (pull)</option>
              <option value="url">Any address (RTSP, UDP, RTP, NDI…)</option>
            </Select>
          </Field>
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></Field>
        </div>
        {protocol === 'rtmp' || protocol === 'url' ? (
          <Field label="Address" hint={protocol === 'url' ? 'As the decoder takes it, e.g. udp://239.1.1.1:5000 or rtsp://host/path' : undefined}>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {protocol === 'srt-caller' && <Field label="Host"><Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="203.0.113.10" /></Field>}
              <Field label={protocol === 'srt-caller' ? 'Port' : 'Listen on port'}><Input value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} /></Field>
              <Field label="Latency (ms)"><Input value={latency} onChange={(e) => setLatency(e.target.value.replace(/\D/g, ''))} /></Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {protocol === 'srt-caller' && <Field label="Stream ID" hint="Optional."><Input value={streamId} onChange={(e) => setStreamId(e.target.value)} /></Field>}
              <Field label="Passphrase" hint={source ? 'Empty: keep what it has.' : 'Optional, 10 to 79 characters.'}><Input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} type="password" autoComplete="new-password" /></Field>
            </div>
          </>
        )}
        {!source && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={select} onChange={(e) => setSelect(e.target.checked)} /> Play it now</label>}
        {err && <Alert tone="critical">{err}</Alert>}
      </div>
    </Modal>
  );
}
